import {Injectable, inject} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ItemCreatedResponse} from '../../entities/base/item-created-response';
import {Medication} from './entities/medication';
import {MedicationCollectionResponse} from './entities/medication-collection-response';
import {MedicationPaginationResponse} from './entities/medication-pagination-response';
import {MedicationTypePaginationResponse} from './entities/medication-type-pagination-response';
import {MedicationType} from './entities/medication-type';
import {MedicationTypeCollectionResponse} from './entities/medication-type-collection-response';
import {ApiService} from '../base/api.service';
import {AdminApiService} from '../base/admin-api.service';
import {GenericPaginationResponse} from '../../entities/base/generic-pagination-response';
import {ItemDeletedResponse} from '../../entities/base/item-deleted-response';
import {ItemUpdatedResponse} from '../../entities/base/item-updated-response';
import {MedicationTypeResponse} from './entities/medication-type-response';
import {ScriptType} from '../scripts-repository/entities/script-type';
import {MedicationSearchResult, MedicationSearchResultResponse} from './entities/medication-search-result';
import {MedicineLibraryProductMatch} from './entities/medicine-library-product-match';
import {MedicationLinkResult} from './entities/medication-link-result';

@Injectable({
  providedIn: 'root'
})
export class MedicationRepositoryService {

  // v2 has the search with the medicine library and the patient list with overlap flags (#333); every other
  // medication call stays on v1.
  private apiV2 = inject(ApiService).version(2);

  constructor(private api: ApiService, private adminApi: AdminApiService) {
  }

  /** v1 search: the practice list only, as a flat list. Used where the library isn't offered (busamed). */
  query(query: string): Observable<Medication[]> {
    return this.api.get<MedicationCollectionResponse>('/medication/search/', {query: query})
      .pipe(map(response => response.items));
  }

  /**
   * v2 search: the practice list, then medicine library products (#333). The practice's library flag decides
   * on the server: with it off, `libraryProducts` is empty and the result is the practice list as before.
   */
  search(query: string): Observable<MedicationSearchResult> {
    return this.apiV2.get<MedicationSearchResultResponse>('/medication/search/', {query: query})
      .pipe(map(response => response.value));
  }

  queryTypes(query: string): Observable<MedicationType[]> {
    return this.api.get<MedicationTypeCollectionResponse>('/medication/types/search/', {query: query})
      .pipe(map(response => response.items));
  }

  loadPatientMedication(patientId: number): Observable<Medication[]> {
    return this.api.get<MedicationCollectionResponse>('/medication/patient/' + patientId)
      .pipe(map(response => response.items));
  }

  /**
   * v2 patient list (#333): the same items as loadPatientMedication, each with `overlap` and `overlapReason`
   * worked out on the server (same drug or same category as another item on the same list).
   */
  loadPatientMedicationWithOverlaps(patientId: number): Observable<Medication[]> {
    return this.apiV2.get<MedicationCollectionResponse>('/medication/patient/' + patientId)
      .pipe(map(response => response.items));
  }

  addMedicationToPatient(patientId: number, medication: Medication) {
    return this.api.post<ItemCreatedResponse>('/medication/patient/' + patientId, medication)
      .pipe(map(response => response.value));
  }

  removeMedicationFromPatient(patientId: number, medicationId: number) {
    return this.api.delete<ItemDeletedResponse>('/medication/patient/' + patientId + '/' + medicationId)
      .pipe(map(response => response));
  }

  createMedicationItem(medication: Medication): Observable<number> {
    return this.api.post<ItemCreatedResponse>('/medication', medication)
      .pipe(map(response => response.value));
  }

  adminPaginate(type: string, pageNumber: number, pageSize: number, filter: string, typeId?: number): Observable<MedicationPaginationResponse> {
    return this.adminApi.get<GenericPaginationResponse<MedicationPaginationResponse>>('/medication/' + type, {pageNumber: pageNumber, pageSize: pageSize, filter: filter, category: typeId})
      .pipe(map(response => response.value));
  }

  adminCreateMedication(medication: Medication): Observable<number> {
    return this.adminApi.post<ItemCreatedResponse>('/medication', medication)
      .pipe(map(response => response.value));
  }

  adminUpdateMedication(id: number, medication: Medication): Observable<boolean> {
    return this.adminApi.put<ItemUpdatedResponse>('/medication/' + id, medication)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  adminDeleteMedication(id: number): Observable<boolean> {
    return this.adminApi.delete<ItemDeletedResponse>('/medication/' + id)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  /** Library products to link a list item to (#333), whatever the practice's library flag. */
  adminSearchLinkProducts(query: string): Observable<MedicineLibraryProductMatch[]> {
    return this.adminApi.get<{items: MedicineLibraryProductMatch[]}>('/medication/link/products', {query})
      .pipe(map(response => response.items ?? []));
  }

  /** Links a list item to a library product, and the practice's rows with the same text (#333). */
  adminLinkMedication(id: number, productId: number): Observable<MedicationLinkResult> {
    return this.adminApi.put<{value: MedicationLinkResult}>('/medication/' + id + '/link', {productId})
      .pipe(map(response => response.value));
  }

  /** Removes a list item's library link, and the links it made on rows with the same text (#333). */
  adminUnlinkMedication(id: number): Observable<MedicationLinkResult> {
    return this.adminApi.delete<{value: MedicationLinkResult}>('/medication/' + id + '/link')
      .pipe(map(response => response.value));
  }

  adminPaginateTypes(type: string, pageNumber: number, pageSize: number, filter: string): Observable<MedicationTypePaginationResponse> {
    return this.adminApi.get<GenericPaginationResponse<MedicationTypePaginationResponse>>('/medication/types/' + type, {pageNumber: pageNumber, pageSize: pageSize, filter: filter})
      .pipe(map(response => response.value));
  }

  adminCreateMedicationType(medicationType: MedicationType): Observable<number> {
    return this.adminApi.post<ItemCreatedResponse>('/medication/types/', medicationType)
      .pipe(map(response => response.value));
  }

  adminUpdateMedicationType(id: number, medicationType: MedicationType): Observable<boolean> {
    return this.adminApi.put<ItemUpdatedResponse>('/medication/types/' + id, medicationType)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  adminDeleteMedicationType(id: number): Observable<boolean> {
    return this.adminApi.delete<ItemDeletedResponse>('/medication/types/' + id)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  adminGetMedicationType(id: number): Observable<MedicationType> {
    return this.adminApi.get<MedicationTypeResponse>('/medication/types/' + id)
      .pipe(map(response => response.value));
  }

  adminQueryTypes(query: string): Observable<MedicationType[]> {
    return this.adminApi.get<MedicationTypeCollectionResponse>('/medication/types/search/', {query: query})
      .pipe(map(response => response.items));
  }

}
